import { useBoolean, useHover, When } from '@teamsparta/react';
import { Flex } from '@teamsparta/stack-flex';
import { vars } from '@teamsparta/stack-tokens';
import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { AlignCenter } from 'public/svg/community/align-center';
import { AlignLeft } from 'public/svg/community/align-left';
import { AlignRight } from 'public/svg/community/align-right';
import { Trash } from 'public/svg/community/trash';
import { useRef } from 'react';

import { Popover, ResizeBar, Skeleton, Tool } from './components';
import {
  useAlignImage,
  useClickOutsideImage,
  useRemoveImage,
  useResizeImage,
} from './logic';

export function CustomizedImage(props: NodeViewProps) {
  const { value: selected, setTrue: select, setFalse: unselect } = useBoolean();
  const { isHovered: hovered, targetRef: imageWrapperRef } =
    useHover<HTMLDivElement>();
  const imageRef = useRef<HTMLImageElement>(null);

  const { startResize } = useResizeImage({ ...props, imageRef });
  const { imagePosition, align } = useAlignImage({ ...props, imageRef });
  const { remove } = useRemoveImage({ ...props });

  const { editable } = props.editor.options;
  const loading = props.node.attrs.loading === 'true';

  useClickOutsideImage({ targetRef: imageRef, onClickOutside: unselect });

  return (
    <Popover.Root open={editable && selected}>
      <Popover.Trigger asChild>
        <NodeViewWrapper
          onClick={select}
          data-drag-handle
          style={{ display: 'flex', justifyContent: imagePosition }}
        >
          <When condition={!loading} fallback={<Skeleton />}>
            <div
              ref={imageWrapperRef}
              style={{ position: 'relative', display: 'inline-block' }}
            >
              <img
                {...props.node.attrs}
                ref={imageRef}
                style={{
                  border:
                    editable && selected
                      ? `2px solid ${vars.brand.default}`
                      : '2px solid transparent',
                }}
              />
              <When condition={editable && (selected || hovered)}>
                <ResizeBar
                  onMouseDown={(event) => startResize(event, 'left')}
                  onTouchStart={(event) => startResize(event, 'left')}
                  position='left'
                />
                <ResizeBar
                  onMouseDown={(event) => startResize(event, 'right')}
                  onTouchStart={(event) => startResize(event, 'right')}
                  position='right'
                />
              </When>
            </div>
          </When>
        </NodeViewWrapper>
      </Popover.Trigger>
      <Popover.Content side='top'>
        <Flex.Row id='image-toolbar' gap={8} align='center' fullWidth>
          <Flex.Row gap={2}>
            <Tool
              onClick={() => align('start')}
              isActive={imagePosition === 'start'}
            >
              <AlignLeft isActive={imagePosition === 'start'} />
            </Tool>
            <Tool
              onClick={() => align('center')}
              isActive={imagePosition === 'center'}
            >
              <AlignCenter isActive={imagePosition === 'center'} />
            </Tool>
            <Tool
              onClick={() => align('end')}
              isActive={imagePosition === 'end'}
            >
              <AlignRight isActive={imagePosition === 'end'} />
            </Tool>
          </Flex.Row>
          <Flex.Row>
            <Tool onClick={remove}>
              <Trash />
            </Tool>
          </Flex.Row>
        </Flex.Row>
      </Popover.Content>
    </Popover.Root>
  );
}
